/* ==========================================================================
   SECTION-COLOR-TAKEOVER-V1
   Scroll engine for the color takeover hero. No libraries (house rule: the
   theme has no GSAP/Lenis/AOS anywhere) — one continuous rAF loop, gated by
   an IntersectionObserver, reads window.scrollY itself. That avoids scroll-
   event jank during iOS momentum scrolling entirely; every frame recomputes
   from the absolute scroll position, so fast scrollers and jump-scrolls can
   never desync the state machine.

   All animation is transform/opacity/custom-property only. The pour layer's
   wavy mask is static; the layer translates (y = pour progress, x = drift)
   and its inner content counter-translates to stay viewport-fixed, which is
   what makes the liquid edge a hard reveal boundary over the product.

   Reduced motion: CSS already swaps the track for static panels via a media
   query; this file additionally refuses to run the loop while it matches.
   ========================================================================== */

(function () {
    const scriptId = document.currentScript && document.currentScript.dataset.id;

    const REST = 0.7;          // fraction of each chapter that rests on the color
    const WAVE_H = 160;        // px, matches the CSS mask tile height
    const WAVE_W = 800;        // px, mask tile period (drift loops over this)
    const DRIFT_SPEED = 0.045; // px per ms of horizontal wave drift

    const reducedMq = window.matchMedia("(prefers-reduced-motion: reduce)");

    const clamp01 = (v) => Math.min(1, Math.max(0, v));
    const smooth = (v) => v * v * (3 - 2 * v);

    function hexToRgb(hex) {
        const h = hex.replace("#", "");
        const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
        const n = parseInt(full, 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }

    const mix = (a, b, t) => [
        Math.round(a[0] + (b[0] - a[0]) * t),
        Math.round(a[1] + (b[1] - a[1]) * t),
        Math.round(a[2] + (b[2] - a[2]) * t),
    ];

    const rgb = (c) => `rgb(${c[0]},${c[1]},${c[2]})`;
    const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

    function init(section) {
        if (!section || section.dataset.ctInitialized) return;
        section.dataset.ctInitialized = "true";

        const jsonEl = section.querySelector(".ctChaptersJSON");
        const track = section.querySelector(".colorTakeoverV1Track");
        const stage = section.querySelector(".colorTakeoverV1Stage");
        const pour = section.querySelector(".colorTakeoverV1Pour");
        const pourInner = section.querySelector(".colorTakeoverV1PourInner");
        if (!jsonEl || !track || !stage || !pour || !pourInner) return;

        // optional start colors: a chapter opens on them and fades to its end
        // colors in sync with the sequence scrub; blank = solid chapter
        const chapters = JSON.parse(jsonEl.textContent).map((c) => {
            const inkRgb =
                c.ink && c.ink.length > 3
                    ? hexToRgb(c.ink)
                    : c.dark
                      ? [252, 252, 252]
                      : [10, 10, 12];
            return {
                ...c,
                bgRgb: hexToRgb(c.bg),
                bgStartRgb: c.bgStart && c.bgStart.length > 3 ? hexToRgb(c.bgStart) : hexToRgb(c.bg),
                inkRgb: inkRgb,
                inkStartRgb: c.inkStart && c.inkStart.length > 3 ? hexToRgb(c.inkStart) : inkRgb,
            };
        });
        const N = chapters.length;
        if (N < 2) return;

        // section-wide behavior settings
        const HOLD = Math.min(0.9, (parseInt(section.dataset.hold, 10) || 0) / 100);
        const SCRUB_WIN = Math.max(0.1, 1 - HOLD);

        // desktop scroll smoothing: the hero's internal progress eases toward
        // the real scroll position (exponential damping) instead of tracking
        // it 1:1. Native scroll is untouched — the page never fights the
        // user, and the damped value always converges on the true position.
        const SMOOTH_TAU =
            (parseInt(section.dataset.smooth, 10) || 0) * 5; /* % -> ms time constant */
        const smoothingOn = () =>
            SMOOTH_TAU > 0 && window.innerWidth > 800 && window.matchMedia("(pointer: fine)").matches;
        let smoothFrac = null;
        let lastNow = null;

        const baseScenes = stage.querySelectorAll('[data-ct-stack="base"] .ctScene');
        const pourScenes = stage.querySelectorAll('[data-ct-stack="pour"] .ctScene');
        const copyBlocks = stage.querySelectorAll(".ctCopyBlock");
        const swatches = stage.querySelectorAll(".ctSwatch");
        const hint = stage.querySelector(".colorTakeoverV1Hint");
        const announcer = section.querySelector("[data-ct-announcer]");

        // sequence canvases grouped per chapter — the base and pour copies are
        // drawn from the same decoded Image objects, so both worlds always
        // show the exact same frame (a video element can't guarantee that)
        const seqCanvases = chapters.map((c, i) =>
            c.sequence
                ? [...section.querySelectorAll(`.ctScene[data-ct-scene="${i}"] canvas[data-ct-seq]`)]
                : null
        );
        const seqState = chapters.map(() => ({ status: "idle", imgs: null, lastIdx: -1 }));

        let trackTop = 0;
        let stageH = 0;
        let scrollRange = 1;
        let inView = false;
        let rafId = null;
        let lastBase = -1;
        let lastPour = -1;
        let lastActive = -1;
        let wasTransitioning = false;
        let hintHidden = false;

        function measure() {
            const rect = track.getBoundingClientRect();
            trackTop = rect.top + window.scrollY;
            stageH = stage.offsetHeight;
            scrollRange = Math.max(1, track.offsetHeight - stageH);
        }

        function ensureSeq(i, priority) {
            if (i < 0 || i >= N || !seqCanvases[i] || !seqCanvases[i].length) return;
            const state = seqState[i];
            if (state.status !== "idle") return;

            const canvas = seqCanvases[i][0];
            let urls;
            try {
                urls = JSON.parse(canvas.dataset.frames).filter(Boolean);
            } catch (e) {
                urls = [];
            }
            if (!urls.length) {
                state.status = "failed";
                return;
            }

            // device-appropriate CDN width: phones never download desktop frames
            const width =
                window.innerWidth <= 800
                    ? canvas.dataset.widthMobile || canvas.dataset.width
                    : canvas.dataset.width;
            if (width) {
                urls = urls.map((u) => u + (u.indexOf("?") > -1 ? "&" : "?") + "width=" + width);
            }

            state.status = "loading";

            // ordered, concurrency-limited loading at low network priority:
            // frames arrive in playback order a few at a time instead of a
            // 25-request burst that starves the hero image on slow networks.
            // Readiness is PROGRESSIVE: the sequence activates once the first
            // frames decode and scrubs within the loaded prefix, catching up
            // as more arrive — on slow connections the animation appears in
            // seconds instead of waiting for every frame. A broken frame
            // permanently caps the prefix (frame 1 broken = sequence off).
            const imgs = new Array(urls.length);
            const done = new Array(urls.length).fill(false);
            let next = 0;
            let brokenAt = -1;
            const CONCURRENCY = 4;

            const advancePrefix = () => {
                let prefix = 0;
                while (prefix < urls.length && done[prefix]) prefix += 1;
                if (brokenAt > -1) prefix = Math.min(prefix, brokenAt);
                if (prefix < 2) return;
                state.imgs = imgs;
                state.loaded = prefix;
                if (state.status !== "ready") {
                    state.status = "ready";
                    const w = imgs[0].naturalWidth;
                    const h = imgs[0].naturalHeight;
                    seqCanvases[i].forEach((cv) => {
                        cv.width = w;
                        cv.height = h;
                        // the media box adopts the frames' aspect so the
                        // animation fills the configured width exactly
                        cv.closest(".ctSceneMedia").style.aspectRatio = `${w} / ${h}`;
                        cv.closest(".ctScene").classList.add("ctSeqReady");
                    });
                    scrubSeq(i, state.lastProgress || 0);
                } else {
                    // newly arrived frames may unlock the frame the scrub wants
                    state.lastIdx = -1;
                    scrubSeq(i, state.lastProgress || 0);
                }
            };

            const loadOne = (idx) =>
                new Promise((resolve) => {
                    const img = new Image();
                    img.fetchPriority = priority || "low";
                    img.decoding = "async";
                    img.onload = () => {
                        img.decode()
                            .then(() => {
                                done[idx] = true;
                            })
                            .catch(() => {
                                brokenAt = brokenAt === -1 ? idx : Math.min(brokenAt, idx);
                            })
                            .then(() => {
                                advancePrefix();
                                resolve();
                            });
                    };
                    img.onerror = () => {
                        brokenAt = brokenAt === -1 ? idx : Math.min(brokenAt, idx);
                        advancePrefix();
                        resolve();
                    };
                    img.src = urls[idx];
                    imgs[idx] = img;
                });

            const worker = () => {
                if (next >= urls.length) return Promise.resolve();
                const idx = next;
                next += 1;
                return loadOne(idx).then(worker);
            };

            const workers = [];
            for (let w = 0; w < CONCURRENCY; w++) workers.push(worker());

            Promise.all(workers).then(() => {
                if (state.status !== "ready") state.status = "failed";
            });
        }

        function scrubSeq(i, progress) {
            const state = seqState[i];
            if (!state) return;
            state.lastProgress = clamp01(progress);
            if (state.status !== "ready") return;
            const frames = state.imgs.length;
            // clamp to the contiguous loaded prefix: on slow connections the
            // scrub tracks whatever has arrived and catches up as frames land
            let idx = frames > 1 ? Math.round(state.lastProgress * (frames - 1)) : 0;
            idx = Math.min(idx, (state.loaded || 0) - 1);
            if (idx < 0 || idx === state.lastIdx) return;
            state.lastIdx = idx;
            seqCanvases[i].forEach((cv) => {
                const ctx = cv.getContext("2d");
                ctx.clearRect(0, 0, cv.width, cv.height);
                ctx.drawImage(state.imgs[idx], 0, 0);
            });
        }

        function setStack(scenes, idx, cache) {
            scenes.forEach((s, j) => {
                if (j === idx) s.setAttribute("data-active", "");
                else s.removeAttribute("data-active");
            });
            return idx;
        }

        function setChrome(ink, chapterBg) {
            section.style.setProperty("--ink", rgb(ink));
            section.style.setProperty("--ink-soft", rgba(ink, 0.75));
            section.style.setProperty("--chapter", rgb(chapterBg));
        }

        function setCopy(i, u) {
            // outgoing copy is gone by u=0.45; incoming starts at u=0.55 —
            // the two are never simultaneously legible mid-pour
            const out = 1 - clamp01(u * 2.2);
            const inn = clamp01((u - 0.55) * 2.2);
            copyBlocks.forEach((el, j) => {
                let o = 0;
                let y = 18;
                if (j === i) {
                    o = out;
                    y = -14 * (1 - out);
                } else if (j === i + 1) {
                    o = inn;
                    y = 18 * (1 - inn);
                }
                el.style.opacity = o;
                el.style.transform = `translate3d(0, ${y}px, 0)`;
                el.classList.toggle("ctLive", o > 0.5);
            });
        }

        function setActive(idx) {
            if (idx === lastActive) return;
            lastActive = idx;
            swatches.forEach((s, j) => {
                s.setAttribute("aria-current", j === idx ? "true" : "false");
            });
            if (announcer) announcer.textContent = chapters[idx].name;
        }

        function frame(now) {
            rafId = inView && !reducedMq.matches ? requestAnimationFrame(frame) : null;

            const rawFrac = clamp01((window.scrollY - trackTop) / scrollRange);
            let frac = rawFrac;
            if (smoothingOn()) {
                const dt = lastNow === null ? 16 : Math.min(100, now - lastNow);
                if (smoothFrac === null) smoothFrac = rawFrac;
                smoothFrac += (rawFrac - smoothFrac) * (1 - Math.exp(-dt / SMOOTH_TAU));
                if (Math.abs(rawFrac - smoothFrac) < 0.0004) smoothFrac = rawFrac;
                frac = smoothFrac;
            } else {
                smoothFrac = rawFrac;
            }
            lastNow = now;

            const p = frac * N;
            const i = Math.min(Math.floor(p), N - 1);
            const t = p - i;
            const hasNext = i < N - 1;
            const transitioning = hasNext && t > REST;
            const u = transitioning ? smooth((t - REST) / (1 - REST)) : 0;

            // scrub progress for sequences/glint/background fade: completes
            // within the first (1 - HOLD) of the rest zone, then holds
            const scrub = clamp01(clamp01(t / REST) / SCRUB_WIN);

            if (i !== lastBase) {
                lastBase = i;
                setStack(baseScenes, i, lastBase);
                ensureSeq(i, "auto");
            }

            // next chapter's frames prefetch at low priority, and only once
            // the visitor is committed to this chapter — not in the initial
            // burst where they would compete with the hero image
            if (t > 0.45) ensureSeq(i + 1, "low");

            // background: fade from the chapter's start color to its end color
            // in lockstep with the sequence scrub (solid when no start color)
            const bgMix = mix(chapters[i].bgStartRgb, chapters[i].bgRgb, smooth(scrub));
            stage.style.backgroundColor = rgb(bgMix);

            // pour world: incoming chapter, poured in at ITS start color
            const next = hasNext ? i + 1 : i;
            if (next !== lastPour) {
                lastPour = setStack(pourScenes, next, lastPour);
                pourInner.style.backgroundColor = rgb(chapters[next].bgStartRgb);
                section.style.setProperty("--pour-hi", chapters[next].highlight);
            }

            // pour position: parked below, or risen by eased progress (+ drift)
            if (transitioning) {
                const py = stageH - (stageH + WAVE_H) * u;
                const dx = (now * DRIFT_SPEED) % WAVE_W;
                pour.style.transform = `translate3d(${dx.toFixed(2)}px, ${py.toFixed(2)}px, 0)`;
                pourInner.style.transform = `translate3d(${(-dx).toFixed(2)}px, ${(-py).toFixed(2)}px, 0)`;
            } else if (wasTransitioning || lastPour !== lastBase) {
                pour.style.transform = `translate3d(0, ${stageH + 4}px, 0)`;
                pourInner.style.transform = "translate3d(0, 0, 0)";
            }

            // chrome recolor: follows the start->end fades at rest, lerps
            // toward the incoming chapter's start colors during the pour
            if (transitioning) {
                setChrome(
                    mix(chapters[i].inkRgb, chapters[i + 1].inkStartRgb, u),
                    mix(chapters[i].bgRgb, chapters[i + 1].bgStartRgb, u)
                );
            } else {
                setChrome(mix(chapters[i].inkStartRgb, chapters[i].inkRgb, smooth(scrub)), bgMix);
            }

            setCopy(i, u);
            setActive(hasNext && t > 0.92 ? i + 1 : i);

            // glint / frame-sequence scrub (hold applies in both modes)
            if (chapters[i].sequence) {
                scrubSeq(i, scrub);
            } else {
                section.style.setProperty("--glintX", `${(-140 + scrub * 420).toFixed(1)}%`);
            }

            if (hint) {
                const shouldHide = p > 0.2;
                if (shouldHide !== hintHidden) {
                    hintHidden = shouldHide;
                    hint.classList.toggle("ctHidden", shouldHide);
                }
            }

            wasTransitioning = transitioning;
        }

        function start() {
            if (rafId === null && inView && !reducedMq.matches) {
                lastNow = null; // don't integrate the time the loop was stopped
                rafId = requestAnimationFrame(frame);
            }
        }

        function stop() {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        }

        // -------------------------------------------------------- navigation ---
        function scrollToChapter(idx) {
            measure();
            // land just past the scrub window, where the chapter has settled
            const rest = Math.min(0.65, REST * SCRUB_WIN + 0.1);
            const target = trackTop + ((idx + rest) / N) * scrollRange;
            window.scrollTo({ top: target, behavior: reducedMq.matches ? "auto" : "smooth" });
        }

        function onSectionClick(event) {
            const swatch = event.target.closest("[data-ct-goto]");
            if (swatch) {
                scrollToChapter(parseInt(swatch.dataset.ctGoto, 10));
                return;
            }
            const cta = event.target.closest("[data-ct-cta]");
            if (cta) {
                // hand the clicked color's variant to the buy boxes below:
                // the owning product reveals its tab group and selects it
                if (cta.dataset.variant) {
                    document.dispatchEvent(
                        new CustomEvent("ohsnap:select-variant", {
                            detail: { variantId: cta.dataset.variant },
                        })
                    );
                }
                const anchor = document.getElementById(cta.getAttribute("href").slice(1));
                if (anchor) {
                    event.preventDefault();
                    window.scrollTo({
                        top: anchor.getBoundingClientRect().top + window.scrollY,
                        behavior: reducedMq.matches ? "auto" : "smooth",
                    });
                }
            }
        }

        // ------------------------------------------------------- tilt shimmer ---
        // Only wired where no permission prompt is needed (per the brief:
        // degrade silently — never ask). If events never fire, nothing shows.
        let tiltWired = false;

        function onTilt(event) {
            if (event.gamma === null || event.beta === null) return;
            if (!tiltWired) {
                tiltWired = true;
                section.classList.add("ctTilting");
            }
            const x = Math.max(-10, Math.min(10, event.gamma / 3));
            const y = Math.max(-10, Math.min(10, (event.beta - 45) / 4));
            section.style.setProperty("--tiltX", `${x.toFixed(1)}px`);
            section.style.setProperty("--tiltY", `${y.toFixed(1)}px`);
        }

        const canTilt =
            typeof window.DeviceOrientationEvent !== "undefined" &&
            typeof window.DeviceOrientationEvent.requestPermission !== "function";

        // ------------------------------------------------------------ wiring ---
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                inView = entry.isIntersecting;
                if (inView) {
                    measure();
                    start();
                } else {
                    stop();
                }
            });
        });

        const onResize = () => {
            measure();
        };
        const onReducedChange = () => {
            stop();
            start();
        };

        measure();
        io.observe(section);

        // warm the first chapter's frames at low priority shortly after init —
        // NOT gated on window load, which on slow connections fires only after
        // every eager image on the page has finished and would delay the warm-up
        // by tens of seconds. Low priority keeps it polite toward the LCP.
        // Skipped while the section isn't rendered (display: none, e.g. hidden
        // by an A/B test arm): nothing could show those frames, and the
        // IntersectionObserver still loads them on demand if it is shown later.
        setTimeout(() => {
            if (section.getClientRects().length) ensureSeq(0, "low");
        }, 2500);
        window.addEventListener("resize", onResize);
        window.addEventListener("load", onResize);
        if (window.visualViewport) window.visualViewport.addEventListener("resize", onResize);
        reducedMq.addEventListener("change", onReducedChange);
        section.addEventListener("click", onSectionClick);
        if (canTilt) window.addEventListener("deviceorientation", onTilt);

        section.classList.add("ctReady");

        section.ctDestroy = function () {
            stop();
            io.disconnect();
            window.removeEventListener("resize", onResize);
            window.removeEventListener("load", onResize);
            if (window.visualViewport) window.visualViewport.removeEventListener("resize", onResize);
            reducedMq.removeEventListener("change", onReducedChange);
            section.removeEventListener("click", onSectionClick);
            if (canTilt) window.removeEventListener("deviceorientation", onTilt);
            delete section.dataset.ctInitialized;
            delete section.ctDestroy;
        };
    }

    function boot() {
        if (scriptId) {
            init(document.querySelector(`[id="${scriptId}"]`));
        } else {
            document.querySelectorAll(".colorTakeoverV1").forEach(init);
        }
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }

    // theme editor lifecycle
    document.addEventListener("shopify:section:load", (event) => {
        const section = event.target.querySelector(".colorTakeoverV1");
        if (section) init(section);
    });

    document.addEventListener("shopify:section:unload", (event) => {
        const section = event.target.querySelector(".colorTakeoverV1");
        if (section && section.ctDestroy) section.ctDestroy();
    });

    document.addEventListener("shopify:section:reorder", () => {
        document.querySelectorAll(".colorTakeoverV1").forEach((section) => {
            if (section.dataset.ctInitialized) {
                const rect = section.getBoundingClientRect();
                void rect; // force a fresh read, then let the next frame re-measure
                window.dispatchEvent(new Event("resize"));
            }
        });
    });
})();
